import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import ImageUploader from '../../components/ImageUploader';
import { alertApprovalIfPending, ApiError, imgSrc } from '../../api/request';
import { listMaterials, MaterialListItem } from '../../api/material';
import { Device, listDevices } from '../../api/device';
import {
    useCategoryTree,
    useCategoryZh,
} from '../../components/MaterialFieldInputs';
import { useCapTypes } from '../../components/CapTypeBadges';
import { i18nText } from '../../utils/i18n';
import { toast } from '../../utils/toast';
import { alertDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import {
    createTip,
    CRAFT_ENUM,
    DESC_MAX,
    LIGHT_ENUM,
    listTipDevices,
    listTipMaterials,
    mountTip,
    ownerSaveCtx,
    SaveTipPayload,
    Tip,
    TIP_TYPE_DESC,
    TIP_TYPES,
    TipDeviceRef,
    TipMaterialRef,
    TipOwner,
    TipOwnerKind,
    tipOwnerLabel,
    TipType,
    TITLE_MAX,
    totalRefCount,
    updateTip,
    isImagePath,
} from '../../api/tips';
import SearchSelect from '../../components/SearchSelect';
import { zhOptions } from '../../utils/searchOptions';

// 加工小贴士的共用弹窗组件: 条目表单 (新建/编辑, 挂载上下文可选)、批量挂载弹窗、
// 引用数下钻弹窗. 材料详情页 / 设备贴士弹窗 (TipsMountCard) 与小贴士库 (TipLibrary) 共用.
// 挂载上下文是"材料或设备" (TipOwner), 表单/提示文案按 owner.kind 取词.

/** 类型标签 (三类不同底色). */
export function TipTypeTag({ type }: { type: string }) {
    const meta = TIP_TYPES.find((t) => t.value === type);
    return (
        <span className={`tag ${meta?.cls || 't-general'}`}>
            {meta?.label || type}
        </span>
    );
}

/** 条目图标: 图片地址显示缩略图; 空不渲染. 库列表/材料详情共用. 非图片值 (2026-10-16 前的存量 emoji) 原样显示字符. */
export function TipIcon({
    icon,
    size = 'sm',
}: {
    icon?: string;
    size?: 'sm' | 'md';
}) {
    const v = (icon || '').trim();
    if (!v) return null;
    if (isImagePath(v)) {
        return (
            <img
                src={imgSrc(v)}
                alt=""
                className={`${size === 'md' ? 'w-7 h-7' : 'w-5 h-5'} inline-block rounded object-contain bg-slate-50 align-text-bottom`}
            />
        );
    }
    return (
        <span
            className={size === 'md' ? 'text-xl' : 'text-base'}
            title={`图标: ${v}`}
        >
            {v}
        </span>
    );
}

function errMsg(e: unknown): string {
    return e instanceof ApiError ? e.message : String(e);
}

// ---- 条目表单 (新建/编辑共用) ---------------------------------------------

interface TipFormProps {
    /** 编辑目标; 不传 = 新建态 */
    tip?: Tip | null;
    /** 新建态的标题初值 (添加弹窗的搜索词带入) */
    presetTitle?: string;
    /** 挂载上下文 (材料或设备); null = 库内编辑 (原地改, 所有引用同步生效) */
    owner?: TipOwner | null;
    onCancel: () => void;
    /** 「← 返回搜索」(仅新建态且有材料上下文时显示) */
    onBack?: () => void;
    /**
     * 保存成功回调. openBulkFor 非空 = 新建了 general 条目,
     * 父组件应打开批量挂载弹窗 (带「条目已创建 · 」前缀).
     */
    onSaved: (openBulkFor?: { tipCode: string; title: string }) => void;
    /** 「到小贴士库编辑 {ID}」链接 (材料上下文 + 引用数 > 1 时) */
    onGotoLib?: (tipCode: string) => void;
}

function sameSet(a: string[], b: string[]): boolean {
    return a.slice().sort().join('|') === b.slice().sort().join('|');
}

export function TipForm({
    tip,
    presetTitle,
    owner,
    onCancel,
    onBack,
    onSaved,
    onGotoLib,
}: TipFormProps) {
    const ownerZh = owner ? tipOwnerLabel(owner.kind) : '';
    const [type, setType] = useState<TipType>(tip?.type || 'special');
    // 图标只收平台存储的图片地址 (2026-10-16 起禁 emoji / 图标 key): 存量非图片值不带入表单, 保存即清空
    const legacyIcon = tip?.icon && !isImagePath(tip.icon) ? tip.icon : '';
    const [icon, setIcon] = useState(legacyIcon ? '' : tip?.icon || '');
    const [title, setTitle] = useState(tip?.title || presetTitle || '');
    const [desc, setDesc] = useState(tip?.description || '');
    const [light, setLight] = useState<string[]>(tip?.light || []);
    const [craft, setCraft] = useState<string[]>(tip?.craft || []);
    const [warn, setWarn] = useState<React.ReactNode>(null);
    // 同名软提示已展示过 → 下次保存带 confirmDup 放行 (切换类型时重置)
    const [dupConfirmed, setDupConfirmed] = useState(false);
    const [busy, setBusy] = useState(false);

    // 有上下文且声明了支持范围时收窄选项 (材料按「适配工艺类型」); color/emboss 不参与收窄
    const ownerLights = owner?.lights;
    const ownerCrafts = owner?.crafts;
    const lightOpts = ownerLights
        ? LIGHT_ENUM.filter(([v]) => ownerLights.includes(v))
        : LIGHT_ENUM;
    const craftOpts = ownerCrafts
        ? CRAFT_ENUM.filter(
              ([v]) =>
                  ownerCrafts.includes(v) || v === 'color' || v === 'emboss',
          )
        : CRAFT_ENUM;

    const toggle = (list: string[], set: (v: string[]) => void, v: string) => {
        set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
    };

    const onSave = async () => {
        const T = title.trim();
        const D = desc.trim();
        if (!T || !D) {
            setWarn(<div className="warnbox">标题与补充说明为必填。</div>);
            return;
        }
        // 编辑态无任何改动 → 不发请求, 关闭并提示「未做改动」
        if (tip) {
            const changed =
                type !== tip.type ||
                icon.trim() !== (tip.icon || '') ||
                T !== tip.title.trim() ||
                D !== tip.description.trim() ||
                !sameSet(light, tip.light) ||
                !sameSet(craft, tip.craft);
            if (!changed) {
                onCancel();
                toast('未做改动');
                return;
            }
        }
        const payload: SaveTipPayload = {
            type,
            icon: icon.trim(),
            title: T,
            description: D,
            light,
            craft,
        };
        if (owner) Object.assign(payload, ownerSaveCtx(owner));
        if (dupConfirmed) payload.confirmDup = true;
        setBusy(true);
        try {
            const { data: res, message } = tip
                ? await updateTip(tip.tipCode, payload)
                : await createTip(payload);
            if (alertApprovalIfPending(message)) {
                // 走审批: 变更未生效, 只关表单 (父组件 reload 拿到的仍是旧数据)
                onSaved();
                return;
            }
            if (res?.needConfirm) {
                // 同名不同说明: 软提示, 再点一次保存放行
                setDupConfirmed(true);
                const dups = res.duplicates || [];
                setWarn(
                    <div className="warnbox">
                        <b>库中已有 {dups.length} 条同名条目</b>：
                        {dups
                            .map((d) => `${d.tipCode}「${d.descSnippet}」`)
                            .join('；')}
                        <br />
                        标题相同、说明不同＝两条不同条目，是允许的。确认无误再点一次保存。
                    </div>,
                );
                return;
            }
            if (res?.reused) {
                // 内容完全命中已有条目 + 材料上下文 → 直接引用
                toast(
                    `库中已有相同条目 ${res.tipCode}，已直接引用（未新建副本）`,
                );
                onSaved();
                return;
            }
            if (res?.forked) {
                // 有挂载上下文 + 引用数>1 → 分叉
                toast(
                    `内容已变更 → 为本${ownerZh}新建 ${res.newTipCode}；其余 ${res.otherRefs} 处引用仍使用 ${tip?.tipCode}`,
                );
                onSaved();
                return;
            }
            if (tip) {
                toast(
                    totalRefCount(tip) > 1
                        ? '已保存（ID 不变，所有引用同步生效）'
                        : '已保存',
                );
                onSaved();
                return;
            }
            // 新建态: general → 自动进入批量挂载
            const code = res?.tipCode || '';
            if (type === 'general') {
                onSaved({ tipCode: code, title: T });
            } else {
                toast(`条目已创建 ${code}`);
                onSaved();
            }
        } catch (e) {
            // 无材料上下文完全重复等业务错误 → 后端 message 直接展示
            setWarn(<div className="warnbox">{errMsg(e)}</div>);
        } finally {
            setBusy(false);
        }
    };

    // 分叉/同步提示按总引用数 (材料 + 设备) —— 与后端 ExecuteUpdate 的判定口径一致
    const ref = tip ? totalRefCount(tip) : 0;
    const refDetail = tip
        ? `${tip.refCount || 0} 个材料 / ${tip.deviceRefCount || 0} 台设备`
        : '';
    const chips = (
        opts: ReadonlyArray<readonly [string, string]>,
        sel: string[],
        onTgl: (v: string) => void,
    ) =>
        opts.length ? (
            opts.map(([v, zh]) => (
                <button
                    key={v}
                    type="button"
                    className={`chip ${sel.includes(v) ? 'on' : ''}`}
                    onClick={() => onTgl(v)}
                >
                    {zh}
                </button>
            ))
        ) : (
            <span className="text-xs text-slate-400">该材料无支持的选项</span>
        );

    return (
        <div className="space-y-4">
            <div>
                <label className="field-label">
                    <span className="text-rose-500">* </span>类型
                </label>
                <div className="space-y-1.5">
                    {TIP_TYPES.map((t) => (
                        <label
                            key={t.value}
                            className={`flex items-start gap-2.5 px-3 py-2 border rounded-lg cursor-pointer ${
                                type === t.value
                                    ? 'border-brand-500 bg-brand-50'
                                    : 'border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                            <input
                                type="radio"
                                name="tf-type"
                                className="mt-1"
                                checked={type === t.value}
                                onChange={() => {
                                    setType(t.value);
                                    setDupConfirmed(false);
                                }}
                            />
                            <div>
                                <span className="text-[13px] font-medium">
                                    {t.label}
                                </span>
                                <span className="text-xs text-slate-500 ml-2">
                                    {TIP_TYPE_DESC[t.value]}
                                </span>
                            </div>
                        </label>
                    ))}
                </div>
            </div>
            <div>
                <label className="field-label">
                    图标（客户端"注意事项"卡片展示）
                </label>
                <ImageUploader
                    value={icon}
                    onChange={(url) => setIcon(url)}
                    placeholder="点击/拖拽上传图标图片"
                    size="md"
                />
                {legacyIcon && !icon && (
                    <div className="text-xs text-amber-600 mt-1.5">
                        原图标「{legacyIcon}」不是图片，已不再支持；请上传图片，否则保存后将清空图标。
                    </div>
                )}
            </div>
            <div>
                <label className="field-label">
                    <span className="text-rose-500">* </span>标题（≤{TITLE_MAX}{' '}
                    字，操作动宾短语）
                </label>
                <input
                    className="inp"
                    value={title}
                    maxLength={TITLE_MAX}
                    onChange={(e) => setTitle(e.target.value)}
                />
                <div className="text-[11px] text-slate-400 mt-1">
                    {title.length}/{TITLE_MAX}
                </div>
            </div>
            <div>
                <label className="field-label">
                    <span className="text-rose-500">* </span>补充说明（≤
                    {DESC_MAX} 字：怎么做 + 为什么/后果）
                </label>
                <textarea
                    className="inp"
                    style={{ height: 84, padding: '8px 10px' }}
                    value={desc}
                    maxLength={DESC_MAX}
                    onChange={(e) => setDesc(e.target.value)}
                />
                <div className="text-[11px] text-slate-400 mt-1">
                    {desc.length}/{DESC_MAX}
                </div>
            </div>
            <div className="border-t border-slate-100 pt-4 grid grid-cols-2 gap-4">
                <div>
                    <label className="field-label">
                        光源
                        {ownerLights && (
                            <span className="text-slate-400">
                                （只列本{ownerZh}支持的）
                            </span>
                        )}
                        <span className="text-slate-400"> · 不选＝全部</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                        {chips(lightOpts, light, (v) =>
                            toggle(light, setLight, v),
                        )}
                    </div>
                </div>
                <div>
                    <label className="field-label">
                        加工类型
                        <span className="text-slate-400"> · 不选＝全部</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                        {chips(craftOpts, craft, (v) =>
                            toggle(craft, setCraft, v),
                        )}
                    </div>
                </div>
            </div>
            {owner && !tip && (
                <div className="text-xs text-slate-500">
                    保存后自动挂载到 <b>{owner.name}</b>{' '}
                    <span className="text-[11px] text-slate-400 font-mono">
                        {owner.key}
                    </span>
                </div>
            )}
            {tip &&
                ref > 1 &&
                (owner ? (
                    // 分叉提示条 (有挂载上下文)
                    <div className="warnbox">
                        这条被 <b>{refDetail}</b> 共用。<b>任何改动</b>
                        都只作用于本{ownerZh}
                        ——保存时会新建一条独立条目并原位替换挂载，其余 {ref -
                            1}{' '}
                        处引用继续使用 <b>{tip.tipCode}</b>。
                        <div className="mt-2">
                            <a
                                className="text-brand-700 font-medium cursor-pointer hover:underline"
                                onClick={() => onGotoLib?.(tip.tipCode)}
                            >
                                想改全部 {ref} 处引用 → 到小贴士库编辑{' '}
                                {tip.tipCode} →
                            </a>
                        </div>
                    </div>
                ) : (
                    // 同步提示条 (无挂载上下文)
                    <div className="warnbox">
                        这条被 <b>{refDetail}</b> 挂载，在小贴士库中修改会
                        <b>全部同步生效</b>
                        。若只想改某一个材料/设备，请到对应详情页编辑。
                    </div>
                ))}
            {warn}
            <div className="flex justify-between pt-3 border-t border-slate-100">
                {!tip && owner && onBack ? (
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onBack}
                    >
                        ← 返回搜索
                    </button>
                ) : (
                    <span />
                )}
                <div className="flex gap-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        disabled={busy}
                        onClick={onCancel}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        disabled={busy}
                        onClick={onSave}
                    >
                        {busy ? '保存中…' : '保存'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ---- 批量挂载弹窗 ----------------------------------------------------------

interface BulkMountModalProps {
    tipCode: string;
    tipTitle: string;
    /** 新建 general 条目保存后自动进入: 标题带「条目已创建 · 」前缀 + 顶部说明条 */
    justCreated?: boolean;
    /** 打开时默认停在哪个维度 (设备贴士弹窗进来时给 'device') */
    initialDim?: TipOwnerKind;
    /** mounted = 本次新增的挂载数 (材料 + 设备; 0 = 未挂载/跳过) */
    onClose: (mounted: number) => void;
}

/** 该材料适配工艺类型徽标 (前 limit 个, 纯展示). */
function CapMiniBadges({
    caps,
    limit = 4,
}: {
    caps: string[];
    limit?: number;
}) {
    const { capTypes } = useCapTypes();
    if (!caps.length) return <span className="text-xs text-slate-300">—</span>;
    const shown = caps.slice(0, limit);
    const extra = caps.length - shown.length;
    return (
        <span className="flex flex-wrap gap-1 justify-end">
            {shown.map((k) => (
                <span key={k} className="tag cap-on">
                    {capTypes.find((c) => c.capKey === k)?.label || k}
                </span>
            ))}
            {extra > 0 && (
                <span className="tag bg-slate-100 text-slate-500">
                    +{extra}
                </span>
            )}
        </span>
    );
}

/** 分类目标: cat1/cat2 均为类别编码 categoryCode (与 material.type1/type2 同口径). */
interface CatTarget {
    cat1: string;
    cat2: string;
}

/** 材料候选池每页条数 (服务端分页, 往下滚加载更多). */
const MAT_PAGE_SIZE = 20;

const catKey = (t: CatTarget) => (t.cat2 ? `${t.cat1}/${t.cat2}` : t.cat1);

/** 材料是否落在该分类目标下 (cat2 空 = 整个一级分类). */
function inCatTarget(m: MaterialListItem, t: CatTarget): boolean {
    return m.type1 === t.cat1 && (t.cat2 === '' || m.type2 === t.cat2);
}

export function BulkMountModal({
    tipCode,
    tipTitle,
    justCreated,
    initialDim = 'material',
    onClose,
}: BulkMountModalProps) {
    const tree = useCategoryTree();
    // 只在自己看得到的维度上挂载 (2026-09-13): 设备管理员不该看到材料清单与材料引用, 反之亦然
    const canSeeMaterial = useCan('material', 'get');
    const canSeeDevice = useCan('device', 'get');
    const dims = [
        canSeeMaterial && 'material',
        canSeeDevice && 'device',
    ].filter(Boolean) as TipOwnerKind[];
    const [dim, setDim] = useState<TipOwnerKind>(
        dims.includes(initialDim) ? initialDim : (dims[0] ?? 'material'),
    );
    // 批量挂载按当前维度的挂载对象判 update (材料 / 设备), 不是小贴士本身的权限 (2026-09-13)
    const canUpdate = useCan(dim, 'update');
    // 材料候选池改服务端分页 + 搜索 (2026-09-13): 材料上千条, 整表拉要十几秒, 弹窗一直"加载中".
    // 首屏 PAGE_SIZE 条, 往下滚加载更多; 搜索与分类筛选都交给后端.
    const [mats, setMats] = useState<MaterialListItem[] | null>(null);
    const [matTotal, setMatTotal] = useState(0);
    const [matPage, setMatPage] = useState(1);
    const [matMore, setMatMore] = useState(false);
    const [matLoading, setMatLoading] = useState(false);
    const [mountedSet, setMountedSet] = useState<Set<string>>(new Set());
    const [c1, setC1] = useState('');
    const [c2, setC2] = useState('');
    const [q, setQ] = useState('');
    const [sel, setSel] = useState<Set<string>>(new Set());
    // 分类目标 (按分类批量挂载): 提交时后端把它展开成该分类下的全部材料
    const [catTargets, setCatTargets] = useState<CatTarget[]>([]);
    // 设备维度 (2026-08-27): 设备 ~70 台, 一次拉全直选, 没有"按分类整片挂"这一层
    const [devs, setDevs] = useState<Device[] | null>(null);
    const [devMountedSet, setDevMountedSet] = useState<Set<string>>(new Set());
    const [devSel, setDevSel] = useState<Set<string>>(new Set());
    const [devQ, setDevQ] = useState('');
    const [busy, setBusy] = useState(false);
    const [loadErr, setLoadErr] = useState('');

    // 已挂载清单 (每条贴士自己的引用, 量小) 单独拉一次
    useEffect(() => {
        let cancelled = false;
        if (canSeeMaterial)
            listTipMaterials(tipCode)
                .then((refs) => {
                    if (cancelled) return;
                    setMountedSet(new Set((refs || []).map((r) => r.xcode)));
                })
                .catch((e) => {
                    if (cancelled) return;
                    setLoadErr(errMsg(e));
                });
        return () => {
            cancelled = true;
        };
    }, [tipCode, canSeeMaterial]);

    // 候选池: 筛选条件变了重拉第一页 (搜索 300ms 防抖)
    useEffect(() => {
        if (!canSeeMaterial) return;
        let cancelled = false;
        const timer = window.setTimeout(
            () => {
                setMatLoading(true);
                listMaterials({
                    page: 1,
                    pageSize: MAT_PAGE_SIZE,
                    keyword: q.trim() || undefined,
                    type1: c1 || undefined,
                    type2: c2 || undefined,
                })
                    .then((res) => {
                        if (cancelled) return;
                        const list = res.list || [];
                        setMats(list);
                        setMatTotal(res.totalCount || 0);
                        setMatPage(1);
                        setMatMore(list.length < (res.totalCount || 0));
                    })
                    .catch((e) => {
                        if (cancelled) return;
                        setMats([]);
                        setLoadErr(errMsg(e));
                    })
                    .finally(() => {
                        if (!cancelled) setMatLoading(false);
                    });
            },
            q ? 300 : 0,
        );
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [canSeeMaterial, q, c1, c2]);

    // 往下滚加载下一页
    const loadMoreMats = useCallback(() => {
        if (matLoading || !matMore) return;
        setMatLoading(true);
        listMaterials({
            page: matPage + 1,
            pageSize: MAT_PAGE_SIZE,
            keyword: q.trim() || undefined,
            type1: c1 || undefined,
            type2: c2 || undefined,
        })
            .then((res) => {
                const list = res.list || [];
                setMats((prev) => {
                    const merged = [...(prev || []), ...list];
                    setMatMore(merged.length < (res.totalCount || 0));
                    return merged;
                });
                setMatTotal(res.totalCount || 0);
                setMatPage((n) => n + 1);
            })
            .catch((e) => setLoadErr(errMsg(e)))
            .finally(() => setMatLoading(false));
    }, [matLoading, matMore, matPage, q, c1, c2]);

    useEffect(() => {
        let cancelled = false;
        if (canSeeDevice)
            Promise.all([
                listDevices({ pageSize: 500 }),
                listTipDevices(tipCode),
            ])
                .then(([page, refs]) => {
                    if (cancelled) return;
                    setDevs(page.list || []);
                    setDevMountedSet(
                        new Set((refs || []).map((r) => r.deviceKey)),
                    );
                })
                .catch((e) => {
                    if (cancelled) return;
                    setDevs([]);
                    setLoadErr(errMsg(e));
                });
        return () => {
            cancelled = true;
        };
    }, [tipCode, canSeeMaterial, canSeeDevice]);

    const l2 = useMemo(
        () => tree.find((t) => t.categoryCode === c1)?.children || [],
        [tree, c1],
    );

    // 分类目标覆盖到的材料 (本地按已拉全的材料列表算, 只为给出「展开成 N 个」的即时预览;
    // 真正的展开在后端提交那一刻重做, 二者口径一致 —— 都是 type1/type2 命中类别编码)
    const catCovered = useMemo(() => {
        const set = new Set<string>();
        if (catTargets.length === 0) return set;
        for (const m of mats || []) {
            if (catTargets.some((t) => inCatTarget(m, t))) set.add(m.xcode);
        }
        return set;
    }, [mats, catTargets]);

    // 分类目标展开后的材料数: 候选池不再整表拉, 逐个目标问服务端要总数 (pageSize=1 只取 totalCount)
    const [catCount, setCatCount] = useState(0);
    useEffect(() => {
        if (catTargets.length === 0) {
            setCatCount(0);
            return;
        }
        let cancelled = false;
        Promise.all(
            catTargets.map((t) =>
                listMaterials({
                    page: 1,
                    pageSize: 1,
                    type1: t.cat1,
                    type2: t.cat2 || undefined,
                })
                    .then((r) => r.totalCount || 0)
                    .catch(() => 0),
            ),
        ).then((counts) => {
            if (!cancelled) setCatCount(counts.reduce((a, b) => a + b, 0));
        });
        return () => {
            cancelled = true;
        };
    }, [catTargets]);

    // 本次真正会新增的挂载 = (分类展开 ∪ 单选) − 已挂载
    const newMountCount = useMemo(() => {
        let n = catCount; // 分类目标展开数 (服务端给, 已挂载的后端提交时会跳过)
        sel.forEach((x) => {
            if (!mountedSet.has(x)) n += 1;
        });
        return n;
    }, [catCount, sel, mountedSet]);

    // 本次真正会新增的设备挂载 = 勾选 − 已挂载
    const newDevMountCount = useMemo(() => {
        let n = 0;
        devSel.forEach((k) => {
            if (!devMountedSet.has(k)) n += 1;
        });
        return n;
    }, [devSel, devMountedSet]);

    // 设备候选池: 排除已挂载该条目的, 支持搜名称/编码/机型
    const devShown = useMemo(() => {
        let list = (devs || []).filter((d) => !devMountedSet.has(d.deviceKey));
        const s = devQ.trim().toLowerCase();
        if (s) {
            list = list.filter((d) =>
                (d.deviceKey + (d.name || '') + d.family + d.archCode)
                    .toLowerCase()
                    .includes(s),
            );
        }
        return list;
    }, [devs, devMountedSet, devQ]);

    // 候选池: 服务端已按分类/关键词筛好, 这里只去掉已挂载该条目的
    const shown = useMemo(
        () => (mats || []).filter((m) => !mountedSet.has(m.xcode)),
        [mats, mountedSet],
    );

    const setAll = (on: boolean) => {
        // 「全选」/「全不选」只作用于已加载出来的那些 (服务端分页, 未加载的不参与)
        setSel((prev) => {
            const next = new Set(prev);
            shown.forEach((m) =>
                on ? next.add(m.xcode) : next.delete(m.xcode),
            );
            return next;
        });
    };

    // 把当前的一级/二级筛选加成分类目标. 同一分类重复添加忽略;
    // 添加一级分类时把它下面已加过的二级目标收掉 (一级已经覆盖了它们, 留着只是噪音).
    const addCatTarget = () => {
        if (!c1) return;
        const t: CatTarget = { cat1: c1, cat2: c2 };
        setCatTargets((prev) => {
            if (prev.some((p) => catKey(p) === catKey(t))) return prev;
            const kept = c2 === '' ? prev.filter((p) => p.cat1 !== c1) : prev;
            return [...kept, t];
        });
        // 已按分类覆盖的单选去掉, 免得同一材料在两处重复计数
        setSel((prev) => {
            const next = new Set(prev);
            for (const m of mats || []) {
                if (inCatTarget(m, t)) next.delete(m.xcode);
            }
            return next;
        });
    };

    const onConfirm = async () => {
        if (catTargets.length === 0 && sel.size === 0 && devSel.size === 0) {
            toast('未选择任何挂载目标');
            return;
        }
        setBusy(true);
        try {
            // 两个维度一次提交 (后端同一事务): 材料 = 分类展开 ∪ 单选, 设备 = 勾选
            const { data: res, message } = await mountTip(tipCode, {
                xcodes: Array.from(sel),
                cats: catTargets.map((t) => ({
                    cat1: t.cat1,
                    cat2: t.cat2 || undefined,
                })),
                deviceKeys: Array.from(devSel),
            });
            if (alertApprovalIfPending(message)) {
                // 走审批: 挂载未生效
                onClose(0);
                return;
            }
            const n = res?.mounted ?? 0;
            const dn = res?.deviceMounted ?? 0;
            const parts = [`${n} 个材料`, `${dn} 台设备`];
            const skipped = (res?.skipped ?? 0) + (res?.deviceSkipped ?? 0);
            toast(
                skipped > 0
                    ? `已挂载到 ${parts.join(' + ')}（另有 ${skipped} 个目标已挂载，跳过）`
                    : `已挂载到 ${parts.join(' + ')}`,
            );
            onClose(n + dn);
        } catch (e) {
            alertDialog(`挂载失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    const { cat1Zh, cat2Zh } = useCategoryZh();
    const catLabel = (t: CatTarget) =>
        t.cat2
            ? `${cat1Zh(t.cat1)} / ${cat2Zh(t.cat1, t.cat2)}`
            : `${cat1Zh(t.cat1)}（全部）`;

    return (
        <Modal
            open
            width={900}
            title={`${justCreated ? '条目已创建 · ' : ''}批量挂载「${tipTitle}」`}
            subtitle="材料侧可按分类整片挂或勾具体材料；设备侧按台勾选（设备自身的注意事项，如清洁镜片/开排烟）"
            onClose={() => onClose(0)}
        >
            <div>
                {justCreated && (
                    <div className="infobox mb-3">
                        <b>{tipCode}</b> 已保存。通用条目的适用范围
                        <b>由挂载关系表达</b>
                        ——挂到哪些材料，它就属于哪些材料。可按分类整片挂，也可勾具体材料。
                    </div>
                )}
                {loadErr && (
                    <div className="warnbox mb-3">
                        候选列表加载失败: {loadErr}
                    </div>
                )}
                {/* 维度切换: 两侧的选择都留着, 确认时一并提交 */}
                <div className="flex items-center gap-2 mb-3 border-b border-slate-100 pb-2">
                    {dims.map((k) => {
                        const n =
                            k === 'material' ? newMountCount : newDevMountCount;
                        return (
                            <button
                                key={k}
                                type="button"
                                className={`text-sm px-3 py-1.5 rounded ${
                                    dim === k
                                        ? 'bg-brand-50 text-brand-700 font-medium'
                                        : 'text-slate-500 hover:bg-slate-50'
                                }`}
                                onClick={() => setDim(k)}
                            >
                                {tipOwnerLabel(k)}
                                {n > 0 && (
                                    <span className="ml-1 text-brand-600">
                                        +{n}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                    <span className="ml-auto text-[11px] text-slate-400">
                        两侧选择互不影响，确认时一并提交
                    </span>
                </div>
                {dim === 'device' && (
                    <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                            <input
                                className="inp w-56"
                                placeholder="搜索设备名 / 编码 / 机型"
                                value={devQ}
                                onChange={(e) => setDevQ(e.target.value)}
                            />
                            <button
                                type="button"
                                className="chip"
                                onClick={() =>
                                    setDevSel((prev) => {
                                        const next = new Set(prev);
                                        devShown.forEach((d) =>
                                            next.add(d.deviceKey),
                                        );
                                        return next;
                                    })
                                }
                            >
                                全选
                            </button>
                            <button
                                type="button"
                                className="chip"
                                onClick={() =>
                                    setDevSel((prev) => {
                                        const next = new Set(prev);
                                        devShown.forEach((d) =>
                                            next.delete(d.deviceKey),
                                        );
                                        return next;
                                    })
                                }
                            >
                                全不选
                            </button>
                            <span className="ml-auto text-xs text-slate-500">
                                已选 <b>{devSel.size}</b> 台 · 当前筛选{' '}
                                {devShown.length} 台
                            </span>
                        </div>
                        <div style={{ maxHeight: '40vh', overflow: 'auto' }}>
                            {devs === null ? (
                                <div className="text-center text-slate-400 text-sm py-8">
                                    加载中…
                                </div>
                            ) : devShown.length === 0 ? (
                                <div className="text-center text-slate-400 text-sm py-8">
                                    没有匹配的设备（已挂载的不在候选中）
                                </div>
                            ) : (
                                devShown.map((d) => (
                                    <label
                                        key={d.deviceKey}
                                        className="flex items-center gap-3 px-3 py-2 border border-slate-100 rounded-lg mb-1.5 hover:bg-slate-50 cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={devSel.has(d.deviceKey)}
                                            onChange={() =>
                                                setDevSel((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(d.deviceKey))
                                                        next.delete(
                                                            d.deviceKey,
                                                        );
                                                    else next.add(d.deviceKey);
                                                    return next;
                                                })
                                            }
                                        />
                                        <span className="text-[13px] flex-1 min-w-0">
                                            {d.name || d.deviceKey}
                                        </span>
                                        <span className="font-mono text-[11px] text-slate-400 w-32">
                                            {d.deviceKey}
                                        </span>
                                        <span className="text-[11px] text-slate-400 w-28">
                                            {d.family} · {d.archCode}
                                        </span>
                                    </label>
                                ))
                            )}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-2">
                            设备侧没有「按分类整片挂」——设备总量只有几十台，直接勾选更直观。
                        </div>
                        <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={busy}
                                onClick={() => {
                                    toast('已跳过批量挂载');
                                    onClose(0);
                                }}
                            >
                                暂不
                            </button>
                            <button
                                type="button"
                                className="btn-primary"
                                disabled={busy}
                                onClick={onConfirm}
                            >
                                {busy
                                    ? '挂载中…'
                                    : `挂载 ${newMountCount} 个材料 + ${newDevMountCount} 台设备`}
                            </button>
                        </div>
                    </div>
                )}
                <div className={dim === 'device' ? 'hidden' : ''}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <SearchSelect
                            className="w-36"
                            value={c1}
                            onChange={(v) => {
                                setC1(v);
                                // 切换一级分类时清空二级选中
                                setC2('');
                            }}
                            placeholder="全部一级分类"
                            options={zhOptions(tree, (t) => t.categoryCode, (t) => t.nameZh)}
                            clearable
                        />
                        <SearchSelect
                            className="w-36"
                            value={c2}
                            disabled={l2.length === 0}
                            onChange={(v) => setC2(v)}
                            placeholder={l2.length ? '全部二级分类' : '—'}
                            options={zhOptions(l2, (n) => n.categoryCode, (n) => n.nameZh)}
                            clearable
                        />
                        <button
                            type="button"
                            className="btn-ghost whitespace-nowrap"
                            disabled={!c1}
                            title={c1 ? '' : '先选一级分类'}
                            onClick={addCatTarget}
                            hidden={!canUpdate}
                        >
                            ＋ 整个分类都挂
                        </button>
                        <input
                            className="inp w-48"
                            placeholder="搜索材料名 / 编码"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                        <button
                            type="button"
                            className="chip"
                            onClick={() => setAll(true)}
                        >
                            全选
                        </button>
                        <button
                            type="button"
                            className="chip"
                            onClick={() => setAll(false)}
                        >
                            全不选
                        </button>
                    </div>

                    {catTargets.length > 0 && (
                        <div className="infobox mb-2 flex flex-wrap items-center gap-1.5">
                            <span className="text-xs">按分类挂载：</span>
                            {catTargets.map((t) => (
                                <span key={catKey(t)} className="chip on">
                                    {catLabel(t)}
                                    <a
                                        className="ml-1.5 cursor-pointer"
                                        title="移除该分类目标"
                                        onClick={() =>
                                            setCatTargets((prev) =>
                                                prev.filter(
                                                    (p) =>
                                                        catKey(p) !== catKey(t),
                                                ),
                                            )
                                        }
                                    >
                                        ×
                                    </a>
                                </span>
                            ))}
                            <span className="text-xs text-slate-500 ml-1">
                                共展开 <b>{catCount}</b> 个材料
                            </span>
                        </div>
                    )}

                    <div className="flex items-center gap-3 mb-2 text-xs text-slate-500">
                        <span>
                            分类目标 <b>{catTargets.length}</b> · 单选材料{' '}
                            <b>{sel.size}</b> · 当前筛选 {matTotal} 个 (已加载{' '}
                            {shown.length})
                        </span>
                        <span className="ml-auto">
                            本次将新增挂载{' '}
                            <b className="text-brand-600">{newMountCount}</b>{' '}
                            个材料
                        </span>
                    </div>

                    <div
                        style={{ maxHeight: '40vh', overflow: 'auto' }}
                        onScroll={(e) => {
                            const el = e.currentTarget;
                            if (
                                el.scrollTop + el.clientHeight >=
                                el.scrollHeight - 80
                            )
                                loadMoreMats();
                        }}
                    >
                        {mats === null ? (
                            <div className="text-center text-slate-400 text-sm py-8">
                                加载中…
                            </div>
                        ) : shown.length === 0 ? (
                            <div className="text-center text-slate-400 text-sm py-8">
                                没有匹配的材料
                            </div>
                        ) : (
                            shown.map((m) => {
                                const byCat = catCovered.has(m.xcode);
                                return (
                                    <label
                                        key={m.xcode}
                                        className={`flex items-center gap-3 px-3 py-2 border border-slate-100 rounded-lg mb-1.5 ${
                                            byCat
                                                ? 'bg-brand-50/40 cursor-default'
                                                : 'hover:bg-slate-50 cursor-pointer'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={byCat || sel.has(m.xcode)}
                                            disabled={byCat}
                                            onChange={() =>
                                                setSel((prev) => {
                                                    const next = new Set(prev);
                                                    if (next.has(m.xcode))
                                                        next.delete(m.xcode);
                                                    else next.add(m.xcode);
                                                    return next;
                                                })
                                            }
                                        />
                                        <span className="font-mono text-xs text-slate-500 w-24">
                                            {m.xcode}
                                        </span>
                                        <span className="text-[13px] flex-1 min-w-0">
                                            {i18nText(m.name)}
                                            {byCat && (
                                                <span className="tag cap-on ml-1.5">
                                                    已按分类选中
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-[11px] text-slate-400 w-24">
                                            {cat1Zh(m.type1) || '—'}
                                            {m.type2
                                                ? ` / ${cat2Zh(m.type1, m.type2)}`
                                                : ''}
                                        </span>
                                        <CapMiniBadges caps={m.caps || []} />
                                    </label>
                                );
                            })
                        )}
                        {mats !== null && (matMore || matLoading) && (
                            <div className="text-center text-slate-400 text-xs py-3">
                                {matLoading ? '加载中…' : '继续下滑加载更多'}
                            </div>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-2">
                        按分类挂载是<b>一次性展开</b>
                        ——落库的是逐个材料的挂载关系，之后新建的材料不会自动带上这条贴士。
                    </div>
                    <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                        <button
                            type="button"
                            className="btn-ghost"
                            disabled={busy}
                            onClick={() => {
                                toast('已跳过批量挂载');
                                onClose(0);
                            }}
                        >
                            暂不
                        </button>
                        <button
                            type="button"
                            className="btn-primary"
                            disabled={busy}
                            onClick={onConfirm}
                        >
                            {busy
                                ? '挂载中…'
                                : `挂载 ${newMountCount} 个材料 + ${newDevMountCount} 台设备`}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

// ---- 引用数下钻弹窗 --------------------------------------------------------

interface TipRefsModalProps {
    tip: Tip;
    /** 打开时默认停在哪个维度 (点「N 台设备」进来时给 'device') */
    initialDim?: TipOwnerKind;
    onClose: () => void;
    /** 点击材料 id: 关闭弹窗并跳转到该材料详情 */
    onOpenMaterial: (xcode: string) => void;
    /** 点击设备编码: 关闭弹窗并跳转到设备管理 (设备无独立详情页) */
    onOpenDevice?: (deviceKey: string) => void;
}

/** 引用下钻: 条目挂在哪些材料 / 哪些设备上 (两个维度分页签, 2026-08-27 加设备侧). */
export function TipRefsModal({
    tip,
    initialDim = 'material',
    onClose,
    onOpenMaterial,
    onOpenDevice,
}: TipRefsModalProps) {
    // 引用明细同样只给自己看得到的维度
    const canSeeMaterial = useCan('material', 'get');
    const canSeeDevice = useCan('device', 'get');
    const dims = [
        canSeeMaterial && 'material',
        canSeeDevice && 'device',
    ].filter(Boolean) as TipOwnerKind[];
    const [dim, setDim] = useState<TipOwnerKind>(
        dims.includes(initialDim) ? initialDim : (dims[0] ?? 'material'),
    );
    const [rows, setRows] = useState<TipMaterialRef[] | null>(null);
    const [devRows, setDevRows] = useState<TipDeviceRef[] | null>(null);
    const [loadErr, setLoadErr] = useState('');
    const { cat1Zh, cat2Zh } = useCategoryZh();

    useEffect(() => {
        let cancelled = false;
        // 两路一起拉: 页签切换不再等接口, 且两个计数都能立刻校准
        Promise.all([
            canSeeMaterial
                ? listTipMaterials(tip.tipCode)
                : Promise.resolve([]),
            canSeeDevice ? listTipDevices(tip.tipCode) : Promise.resolve([]),
        ])
            .then(([mats, devs]) => {
                if (cancelled) return;
                setRows(mats || []);
                setDevRows(devs || []);
            })
            .catch((e) => {
                if (cancelled) return;
                setRows([]);
                setDevRows([]);
                setLoadErr(errMsg(e));
            });
        return () => {
            cancelled = true;
        };
    }, [tip.tipCode]);

    const matN = rows === null ? tip.refCount || 0 : rows.length;
    const devN = devRows === null ? tip.deviceRefCount || 0 : devRows.length;

    return (
        <Modal
            open
            width={640}
            title={`「${tip.title}」的引用 · ${matN} 个材料 / ${devN} 台设备`}
            subtitle="点击材料/设备可跳转过去；设备侧的挂载在「设备管理 → 小贴士」维护"
            onClose={onClose}
        >
            <div>
                {loadErr && (
                    <div className="warnbox mb-3">加载失败: {loadErr}</div>
                )}
                <div className="flex items-center gap-2 mb-3 border-b border-slate-100 pb-2">
                    {dims.map((k) => (
                        <button
                            key={k}
                            type="button"
                            className={`text-sm px-3 py-1.5 rounded ${
                                dim === k
                                    ? 'bg-brand-50 text-brand-700 font-medium'
                                    : 'text-slate-500 hover:bg-slate-50'
                            }`}
                            onClick={() => setDim(k)}
                        >
                            {tipOwnerLabel(k)}（{k === 'material' ? matN : devN}
                            ）
                        </button>
                    ))}
                </div>
                <div
                    className="space-y-1.5"
                    style={{ maxHeight: '52vh', overflow: 'auto' }}
                >
                    {dim === 'material' ? (
                        rows === null ? (
                            <div className="text-center text-slate-400 text-sm py-6">
                                加载中…
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="text-center text-slate-400 text-sm py-6">
                                没有材料挂载这条
                            </div>
                        ) : (
                            rows.map((r) => (
                                // 整行都是跳转热区 (点行内任意位置即可, 不用瞄准编码那几个字)
                                <div
                                    key={r.xcode}
                                    className="flex items-center justify-between px-3 py-2 border border-slate-100 rounded-lg cursor-pointer hover:bg-slate-50 hover:border-brand-200"
                                    title={`打开材料 ${r.xcode}`}
                                    onClick={() => onOpenMaterial(r.xcode)}
                                >
                                    <div>
                                        <span className="text-brand-600 font-mono text-xs">
                                            {r.xcode}
                                        </span>
                                        <span className="text-[13px] ml-2">
                                            {i18nText(r.name)}
                                        </span>
                                    </div>
                                    <span className="text-[11px] text-slate-400">
                                        {cat1Zh(r.type1)}
                                        {r.type2
                                            ? ` / ${cat2Zh(r.type1, r.type2)}`
                                            : ''}
                                    </span>
                                </div>
                            ))
                        )
                    ) : devRows === null ? (
                        <div className="text-center text-slate-400 text-sm py-6">
                            加载中…
                        </div>
                    ) : devRows.length === 0 ? (
                        <div className="text-center text-slate-400 text-sm py-6">
                            没有设备挂载这条
                        </div>
                    ) : (
                        devRows.map((d) => (
                            <div
                                key={d.deviceKey}
                                className={`flex items-center justify-between px-3 py-2 border border-slate-100 rounded-lg ${
                                    onOpenDevice
                                        ? 'cursor-pointer hover:bg-slate-50 hover:border-brand-200'
                                        : ''
                                }`}
                                title={
                                    onOpenDevice
                                        ? `在设备管理中打开 ${d.deviceKey}`
                                        : undefined
                                }
                                onClick={
                                    onOpenDevice
                                        ? () => onOpenDevice(d.deviceKey)
                                        : undefined
                                }
                            >
                                <div>
                                    <span
                                        className={`font-mono text-xs ${
                                            onOpenDevice
                                                ? 'text-brand-600'
                                                : 'text-slate-500'
                                        }`}
                                    >
                                        {d.deviceKey}
                                    </span>
                                    <span className="text-[13px] ml-2">
                                        {d.name || d.deviceKey}
                                    </span>
                                </div>
                                <span className="text-[11px] text-slate-400">
                                    {d.family} · {d.archCode}
                                </span>
                            </div>
                        ))
                    )}
                </div>
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                    >
                        关闭
                    </button>
                </div>
            </div>
        </Modal>
    );
}
